<script>
/**
 * QuickBooks venues
 * @version 1.0.0
 * @since
 */

import quickBooksGuard from '@/views/platform/venue/quick-books/settings/quickBooksGuard';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch/index';
import EzTable from '@/components/ui/Table/EzTable';
import EzLoadMore from '@/components/ui/LoadMore/EzLoadMore.vue';
import EzSpinner from '@/components/ui/Spinner/EzSpinner.vue';
import VVenueEntityInfo from '@/components/v3/patterns/VVenueEntityInfo/index';
import {
  venueGetVenues,
  venueGetExternalVenues,
  venueQuickBooksAddVenue,
  venueGetQuickBooksTrackingCategories,
  venueGetQuickBooksAccounts,
  venueGetQuickBooksTaxesSetup,
} from '@/api/endpoints/quick-books';
import flash from '@/components/ui/FlashMessage';
import EmptyState from '@/views/common/empty-state/EmptyState';
import EzLoader from '@/components/ui/Loader/EzLoader';
import { mapGetters } from 'vuex';
import { LOADING_KEY, SELECT_DESELECT_ALL } from '@/util/constants';
import { debounce } from '@/util/utils';
import EzFilterList from '@/components/ui/FilterList';
import EzInput from '@/components/ui/Input/EzInput.vue';
import FooterForUpdate from "@/views/platform/venue/xero/settings/FooterForUpdate";
import UpdateModal from "@/views/platform/venue/quick-books/settings/UpdateModal";
import EzCheckbox from "@/components/ui/Checkbox/Checkbox";

export default {
  mixins: [quickBooksGuard],
  components: {
    VSelectSearch,
    EzTable,
    EzLoadMore,
    EzSpinner,
    VVenueEntityInfo,
    EmptyState,
    EzLoader,
    FooterForUpdate,
    UpdateModal,
    EzCheckbox,
    EzFilterList,
    EzInput,
  },
  props: {
    venueId: {
      type: Number,
    },
  },
  data() {
    return {
      isLoaderShown: false,
      isLoadingMore: false,
      buttonDisabled: true,
      contacts: [],
      venues: [],
      meta: {},
      columns: [],
      columnsHeaders: {},
      trackingCategories: [],
      accountsData: {},
      taxes: [],
      selectedVenues: [],
      updateData: {},
      selected: false,
      listOfvenueIds: [],
      filters: {
        term: null,
      },
    };
  },
  computed: {
    ...mapGetters('loading', ['isSomeLoading']),
    isLoadingQB() {
      return this.isSomeLoading([LOADING_KEY.VENUE_UPDATE_QUICK_BOOKS_SUPPLIERS]);
    },
    columnsNested() {
      return [
        'name',
        ...this.columns,
        'quickBooksVendor'
      ];
    },
    tableHeaders() {
      return {
        name: this.venueNameCheckbox,
        ...this.columnsHeaders,
        quickBooksVendor: () => 'QuickBooks Vendor',
      };
    },
    entity() {
      return this.$t('global.venues');
    },
    accounts() {
      return this.accountsData?.accounts?.length;
    },
    checkedVenues() {
      return this.selectedVenues.filter(sv => sv.checked);
    },
    uncheckedVenues() {
      return this.selectedVenues.filter(sv => !sv.checked);
    },
    checkedVenuesCount() {
      return this.selected
        ? this.meta.totalCount - this.listOfvenueIds.length
        : this.listOfvenueIds.length;
    },
    indeterminate() {
      return (
        this.checkedVenuesCount > 0 && this.checkedVenuesCount !== this.meta.totalCount
      );
    },
    areAllSelected() {
      return this.checkedVenuesCount === this.meta.totalCount;
    },
    venuesToUpdate() {
      const venues = this.selected ? this.uncheckedVenues : this.checkedVenues;
      return venues.map(vn => ({
        groupVenueId: vn.venueId,
        externalId: vn.integrationEntity?.id || null,
      }));
    },
    isLoading() {
      return this.isLoaderShown || this.isLoadingQB;
    },
  },
  methods: {
    openUpdateModal() {
      this.$refs.updateModal.open();
    },
    vendorSelected(contact, venueId) {
      this.saveChanges({
        id: venueId,
        contactId: contact.id ? contact.id : null,
      });
    },
    async saveChanges(item) {
      try {
        this.isLoaderShown = true;
        await venueQuickBooksAddVenue(this.venueId, {
          items: [
            {
              externalId: item.contactId,
              groupVenueId: item.id,
            },
          ],
        });
        const index = this.venues.findIndex(venue => venue.id === item.id);
        if (item.contactId) {
          this.venues[index].integrationEntity = {
            id: item.contactId,
            name: item.contactName,
          };
        } else {
          this.venues[index].integrationEntity = null;
        }
        this.isLoaderShown = false;
        flash.success({
          title: this.$t('quickBooks.settings.venues.flashMessages.success.title'),
        });
      } catch (e) {
        this.isLoaderShown = false;
        flash.error({
          title: this.$t('quickBooks.settings.venues.flashMessages.error.title'),
        });
      }
    },
    async getVenuesListAndContacts() {
      this.isLoaderShown = true;
      const { data: contacts } = await venueGetExternalVenues(this.venueId);
      this.contacts = contacts.data;

      const { data: venues } = await venueGetVenues({
        id: this.venueId,
        ...this.filters,
      });
      this.meta = venues.meta;
      this.venues = venues.data;
      this.columns = venues.meta.columns?.map(column => column.key) ?? [];
      this.columns.forEach(item => {
        this.columnsHeaders[item.key] = () => item.name;
      });
      this.venues.forEach(item => {
        if (this.selectedVenues.findIndex(x => x.venueId === item.id) === -1) {
          this.selectedVenues.push({
            ...item,
            venueId: item.id,
            checked: this.selected,
          });
        }
      });
      this.isLoaderShown = false;
    },
    async fetchMoreVenues() {
      this.isLoadingMore = true;
      const { data: venues } = await venueGetVenues({
        id: this.venueId,
        ...this.meta,
        ...this.filters,
      });
      this.venues = [...this.venues, ...venues.data];
      this.venues.forEach(item => {
        if (this.selectedVenues.findIndex(x => x.venueId === item.id) === -1) {
          this.selectedVenues.push({
            ...item,
            venueId: item.id,
            checked: this.selected,
          });
        }
      });
      this.meta = venues.meta;
      this.isLoadingMore = false;
    },
    async updateVenues(ev) {
      this.config = ev;
      this.updateData = {
        ...this.updateData,
        config: this.config,
        state: this.selected ? SELECT_DESELECT_ALL.selectedAll : SELECT_DESELECT_ALL.deselectedAll,
      };
      await venueQuickBooksAddVenue(
        this.venueId,
        this.updateData,
        this.filters.term,
        this.filters.operative,
      );
      await this.getVenuesListAndContacts();
      this.$refs.updateModal.close();
    },
    async resetFilters() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = null;
      });
      await this.getVenuesListAndContacts();
    },
    updateFilters: debounce(async function deb(filterName, event) {
      if (filterName === 'search') {
        if (event.length !== 0 && event.length < 3) return;
        this.filters = { ...this.filters, term: event.length ? event : null };
      }
      this.selectedVenues = [];
      await this.getVenuesListAndContacts();
    }, 300),
    isChecked(row) {
      return this.selectedVenues?.find(sp => sp.venueId === row.id)?.checked;
    },
    getTrackingValue(item, col) {
      return item?.tracking?.[col]?.name || '-';
    },
    venueNameCheckbox(h) {
      return h('span', {class: 'u-flex-center select-all-checkbox'}, [
        h(
          'ez-checkbox',
          {
            class: 'cursor-pointer',
            props: {
              indeterminate: this.indeterminate,
              label: `${this.$t('global.venue')}`,
              checked: this.areAllSelected,
            },
            on: {
              change: ev => {
                this.selectDeselectAll(ev);
              },
            },
          },
          [],
        ),
      ]);
    },
    selectDeselectAll(ev) {
      this.selected = ev;
      this.listOfvenueIds = [];
      this.selectedVenues = this.selectedVenues.map(cv => ({...cv, checked: ev}));
    },
    onCheckboxChange(pr, checked) {
      if (this.selected !== checked) {
        this.listOfvenueIds.push({
          venueId: pr.id,
          ...(pr.integrationEntity?.id
            ? {externalId: pr.integrationEntity.id}
            : {externalId: null}),
        });
      } else {
        this.listOfvenueIds = this.listOfvenueIds.filter(
          item => item.venueId !== pr.id,
        );
      }
      this.selectedVenues.find(pro => pro.venueId === pr.id).checked = checked;
    },
    async getTrackingCategories() {
      const {
        data: {data},
      } = await venueGetQuickBooksTrackingCategories(this.venueId, {withDefault: true});
      this.trackingCategories = data;
    },
    async getAccounts() {
      const {
        data: {data: accounts},
      } = await venueGetQuickBooksAccounts(this.venueId, {withDefault: true});
      this.accountsData = accounts;
      this.accountsData = {
        accounts,
      };

      const {
        data: {data: taxes},
      } = await venueGetQuickBooksTaxesSetup(this.venueId, {withDefault: true});
      this.taxes = taxes;
    },
  },
  created() {
    this.getVenuesListAndContacts();
    this.getTrackingCategories();
    this.getAccounts();
  },
  watch: {
    venuesToUpdate() {
      this.updateData = {
        ...this.updateData,
        items: this.venuesToUpdate,
      };
    },
    checkedVenues(val) {
      window.Intercom('update', {
        hide_default_launcher: !!val.length,
      });
    },
  },
};
</script>
<template>
  <div>
    <div class="header mb-24">
      <span class="header__info-text">
        To edit tracking categories, select the
        {{ $t('global.venues') | lowercase }} you want to update and click on the “Update
        {{ $t('global.venues') }}” button at the bottom.
      </span>
    </div>
    <ez-filter-list
      :filters="filters"
      @filterUpdated="updateFilters"
      @resetFilter="resetFilters"
      class="mb-24"
    >
      <ez-input
        formKey="filters"
        name="search"
        class="search"
        :placeholder="`Search for a ${$t('global.venue')}`"
      >
        <template #prefix>
          <font-awesome-icon icon="search" />
        </template>
      </ez-input>
    </ez-filter-list>
    <div>
      <ez-table
        v-if="venues.length"
        class="sync-table"
        :disable-hover="true"
        :data="venues"
        :columns="columnsNested"
        :headers="tableHeaders"
        :columnProps="{
          name: { class: 'venue-cell' },
          quickBooksVendor: { class: 'xxl-cell' },
        }"
      >
        <template #cell-name="{ row }">
          <div class="venue-name">
            <ez-checkbox
              class="cursor-pointer mr-8"
              :key="row.id"
              @change="onCheckboxChange(row, $event)"
              :checked="isChecked(row)"
            />
            <v-venue-entity-info
              class="cursor-pointer"
              :venue="row"
              @click.native="onCheckboxChange(row, !isChecked(row))"
            />
          </div>
        </template>
        <template v-for="cl in columns" #[`cell-${cl}`]="{ row }">
          {{ getTrackingValue(row.integrationConfig, cl) }}
        </template>
        <template #cell-quickBooksVendor="{ row }">
          <v-select-search
            :data="contacts"
            :search="`/venues/${venueId}/quick-books/external-venues`"
            value-property="id"
            placeholder="Select QuickBooks Vendor"
            :selected="row.integrationEntity?.id ?? null"
            @selected="obj => vendorSelected(obj, row.id)"
          />
        </template>
      </ez-table>
      <template v-if="!venues.length">
        <empty-state>
          <template #badge>
            <img src="@/assets/no-venue-empty-state.svg" width="160" alt="" />
          </template>
          <template #title>{{ $t('quickBooks.settings.venues.emptyState.title') }}</template>
        </empty-state>
      </template>
      <div v-if="isLoadingMore" class="u-text-center mt-12">
        <ez-spinner />
      </div>
      <ez-load-more
        v-if="meta.nextId && meta.nextValue && !isLoadingMore"
        @loadMore="fetchMoreVenues"
      />
      <update-modal
        v-if="accounts"
        ref="updateModal"
        :entity="entity"
        :categories="trackingCategories"
        :acc-data="accountsData"
        :taxes="taxes"
        @update="updateVenues"
      />
      <footer-for-update
        v-if="checkedVenuesCount"
        :items-count="checkedVenuesCount"
        :entity="entity"
        @footerAction="openUpdateModal"
      />
      <ez-loader :show="isLoading">Loading...</ez-loader>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.sync-table tbody tr td {
  overflow: visible;
}
.venue-name {
  display: flex;
  align-items: center;
}
.venue-cell {
  width: 500px;
}
.select-all-checkbox {
  .input-group {
    @include font-size(12px);
    :deep() .check-indicator {
      margin-right: 8px;
    }
  }
}
.header {
  width: 400px;
  &__info-text {
    @include font-size(14px, 20px);
    font-weight: 500;
    color: $color-gray-6C;
  }
}

:deep() .ez-filter-list__items .search {
  width: 260px;
}

:deep() .select-search__trigger {
  min-width: 220px;
}
</style>
